<template>
  <aw-cms-page-sidebar-wrapper :menu-items="menuItems" :selected-item="currentItem" @navigate="clickOnPetrolMenu">
    <template #content>
      <slot />
    </template>
  </aw-cms-page-sidebar-wrapper>
</template>

<script setup>
  import { computed } from 'vue';
  import { useNuxtApp, useRoute } from 'nuxt/app';
  import { useContentMenusStore } from '~~/common/stores/contentMenus';
  import { useContentMenu } from '~~/common/composables/useContentMenu';
  import { usePetrolBreadcrumbs } from '~~/common/composables/usePetrolBreadcrumbs';
  import { removeLocaleFromUrl } from '~~/common/utils/urlTransformation';
  import { useContentsStore } from '~~/common/stores/contents.js';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb.js';
  import AwCmsPageSidebarWrapper from '~~/common/components/Page/Content/Wrapper/Sidebar/AwCmsPageSidebarWrapper.vue';

  // A petrol station profile URL: `/benzinkutak/<slug>` (`/petrol/<slug>` on en).
  const PETROL_STATION_PATH_REGEX = /^\/(benzinkutak|petrol)\/[^/]+$/;

  const props = defineProps({
    petrolStation: {
      type: Object,
      default: () => ({}),
    },
  });

  const { $awt } = useNuxtApp();
  const route = useRoute();
  const contentMenuStore = useContentMenusStore();
  const breadCrumbStore = useBreadcrumbStore();
  const { petrolHomeBreadcrumb } = usePetrolBreadcrumbs();

  const menuItems = computed(() => contentMenuStore.petrolMenu.map((item, index) => ({
    id: `${item.to}-${index}`,
    name: item.label,
    link: item.to,
  })));

  const currentItem = computed(() => menuItems.value.find(item => isActive(item)));

  await useContentMenu('petrol-menu');

  // loadBreadcrumb() cannot know the CMS landing URL, so the "Benzinkutak" level
  // arrives linkless; now that the petrol-menu is loaded, point it at the landing.
  if (contentMenuStore.petrolHomeLink) {
    const petrolHomeToken = $awt('aw.pages.petrol.title');
    breadCrumbStore.setBreadcrumbs(breadCrumbStore.breadcrumbs.map(breadcrumb => (
      breadcrumb.token === petrolHomeToken ? petrolHomeBreadcrumb(false) : breadcrumb
    )));
  }

  function removeTrailingSlash (url) {
    return url.replace(/\/$/, '');
  }

  function isActive (menuItem) {
    const currentPath = removeTrailingSlash(removeLocaleFromUrl(route.path));
    const menuPath = removeTrailingSlash(removeLocaleFromUrl(menuItem.link ?? ''));
    if (currentPath === menuPath) {
      return true;
    }
    // The station finder menu item points at one specific station, but it stays
    // active while any other station is selected from the finder's dropdown.
    return (
      Boolean(props.petrolStation?.slug) &&
      PETROL_STATION_PATH_REGEX.test(currentPath) &&
      PETROL_STATION_PATH_REGEX.test(menuPath)
    );
  }

  function clickOnPetrolMenu (menuItem) {
    const contentsStore = useContentsStore();
    contentsStore.setNeedPetrolContent(true);

    breadCrumbStore.setBreadcrumbs([
      {
        token: $awt('aw.common.category.home'),
        to: '/',
        disabled: false,
      },
      petrolHomeBreadcrumb(false),
      {
        token: menuItem.name,
        to: menuItem.link,
        disabled: true,
      },
    ]);
  }
</script>
